// WHAT: Component displaying roadmap items distribution by type with progress bars
// WHY:  Provides visual representation of item type distribution for analytics dashboard
// DOMAIN: Admin → Roadmap → Analytics
// DEPENDENCIES: Card, Badge, ProgressBar components, Lucide icons
// RISKS:
//   - NOTE: Division by zero if total is 0 - percentage calculation will be NaN
//   - NOTE: Dynamic Tailwind classes may not work with JIT compiler
//   - NOTE: Unknown types get default styling - may not be visually distinct

import { LucideBarChart3 } from "lucide-react";
import React from "react";

import { Card, Badge, ProgressBar } from "@/components/ui";

/**
 * Props for TypeDistribution component
 */
interface TypeDistributionProperties {
  /** Object mapping type names to item counts */
  byType: Record<string, number>;
  /** Total number of items for percentage calculation */
  total: number;
}

/**
 * WHAT:
 *   Returns configuration (variant, color) for a given type.
 *
 * WHY:
 *   Centralizes type-to-visual-configuration mapping for consistency.
 *
 * BEHAVIOR:
 *   - Maps known types to specific variants and colors
 *   - Returns default configuration for unknown types
 *
 * RISKS:
 *   - NOTE: Unknown types get default config - may not be visually distinct
 *
 * @param type - Type string to get configuration for
 *
 * @returns {object} Configuration object with variant and color
 *
 */
const getTypeConfig = (type: string) => {
  switch (type) {
    case "feature": {
      return { color: "tracq-bright-blue", variant: "primary" as const };
    }
    case "bug": {
      return { color: "red-500", variant: "destructive" as const };
    }
    case "insight": {
      return { color: "tracq-deep-orange", variant: "accent" as const };
    }
    case "idea": {
      return { color: "orange-500", variant: "warning" as const };
    }
    default: {
      return { color: "slate-500", variant: "default" as const };
    }
  }
};

/**
 * WHAT:
 *   Component that displays a breakdown of roadmap items by type with
 *   progress bars showing the percentage distribution.
 *
 * WHY:
 *   Provides visual insight into how roadmap items are distributed across
 *   different types (feature, bug, insight, idea), helping identify type patterns.
 *
 * PROPS:
 *   - byType: Record<string, number> — Type name to item count mapping
 *   - total: number — Total item count for percentage calculation
 *
 * BEHAVIOR:
 *   - Maps type strings to variants and colors
 *   - Calculates percentage for each type (count / total * 100)
 *   - Displays progress bar and percentage text
 *   - Handles unknown types with default configuration
 *
 * RISKS:
 *   - NOTE: Division by zero if total is 0 - add validation or default value
 *   - NOTE: Dynamic Tailwind classes may not be purged correctly - use static classes
 *   - NOTE: Unknown types get default styling - may not be visually distinct
 *
 * @param root0
 *
 * @param root0.byType
 *
 * @param root0.total
 *
 * @returns {JSX.Element} Die gerenderte TypeDistribution-Komponente
 *
 */
export const TypeDistribution: React.FC<TypeDistributionProperties> = ({ byType, total }) => {
  return (
    <Card padding="lg" variant="default">
      <div className="flex items-center gap-2 mb-6">
        <LucideBarChart3 className="w-5 h-5 text-tracq-deep-orange" />
        <h3 className="text-lg font-medium text-white">Type Distribution</h3>
      </div>

      <div className="space-y-4">
        {Object.entries(byType).map(([type, count]) => {
          const typeConfig = getTypeConfig(type);
          const percentage = (count / total) * 100;

          return (
            <div className="space-y-2" key={type}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full bg-${typeConfig.color}`} />
                  <Badge size="sm" style="subtle" variant={typeConfig.variant}>
                    {type}
                  </Badge>
                </div>
                <span className="text-sm text-slate-400 font-medium">
                  {count} ({percentage.toFixed(1)}%)
                </span>
              </div>
              <ProgressBar
                max={100}
                showLabel={false}
                size="sm"
                state={typeConfig.variant === "destructive" ? "error" : "default"}
                value={percentage}
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
};
